import { create } from 'zustand';
import { createJSONStorage, persist } from 'zustand/middleware';
import { broadcastAuth } from '../lib/auth-broadcast';
import { limparTodosArquivos, tenantDoToken as tenantDoTokenAtual } from '../lib/arquivo-persistido';

/**
 * Estado global de autenticação (Zustand) — REQ-003 / AB#23-AB#25.
 *
 * Persiste APENAS refresh_token + usuario + autenticado em localStorage para
 * sobreviver a F5. O access_token NUNCA é persistido (REQ-003 RN-05) — após
 * reload, a primeira requisição via apiFetch dispara um /auth/refresh e
 * obtém um novo access_token. Endurecimento futuro (refresh em httpOnly
 * cookie) está previsto no REQ-003.
 */

const API_URL = `${import.meta.env.VITE_API_URL ?? 'http://localhost:8000'}/api`;

export interface UsuarioAtual {
  id: number;
  nome: string;
  email: string;
  perfil: string;
  /** [AB#159] Flags JSON granulares (REQ-005 / AB#31). Frontend lê
   *  pra decidir UX condicional — ex.: `ver_todas_conversas` libera
   *  a tab "Todas" no inbox. `{}` quando o backend não envia (login
   *  antigo / refresh fora do fluxo /auth/me). */
  flags?: Record<string, unknown>;
  /** [AB#185 / ADR-030] Unidade preferida do usuário — default dos
   *  filtros de estabelecimento (Agenda/Kanban/Inbox/Dashboard).
   *  `null` ou ausente = sem preferência (filtros começam em
   *  "Todos"). Setado em /minha-conta/perfil. */
  estabelecimento_preferencial_id?: number | null;
}

interface Sessao {
  access_token: string;
  refresh_token: string;
  usuario: UsuarioAtual;
  /** [Bug#357] true quando o login devolveu `trial_expirado` no corpo. */
  trial_expirado?: boolean;
  /** [AB#1282] true quando a conta ainda está com a senha provisória (criada direto pelo admin). */
  trocar_senha_obrigatoria?: boolean;
}

/** Resultado de refreshSession — permite distinguir inatividade de outras falhas. */
export type RefreshResult = { ok: true } | { ok: false; reason: 'inactivity' | 'invalid' };

interface AuthState {
  access_token: string | null;
  refresh_token: string | null;
  usuario: UsuarioAtual | null;
  autenticado: boolean;
  /**
   * [Bug#357] Tenant com o trial vencido. Serve pro `TenantLayout` barrar a rota
   * ANTES de montar — sem isso a página do módulo montava, disparava request,
   * tomava 403 e só então o interceptor redirecionava (o "flash").
   *
   * É um CACHE, não a fonte da verdade — quem protege é o backend. Por isso é
   * auto-corrigível: o `TrialBanner` já chama `/auth/me` no shell e sincroniza
   * a flag; e o `assinarPacote` a zera na hora. Sem isso, um cliente que ACABOU
   * DE PAGAR ficaria com a flag velha e seria expulso de toda rota pra sempre.
   */
  trial_expirado: boolean;
  /** [AB#1282] Conta com senha provisória: tudo, menos a troca de senha, devolve 403 até trocar. */
  senha_provisoria: boolean;
  setSenhaProvisoria: (v: boolean) => void;
  setSession: (sessao: Sessao) => void;
  /** [Bug#357] Sincroniza a flag (do /auth/me ou após assinar um pacote). */
  setTrialExpirado: (v: boolean) => void;
  /** Limpa o estado localmente (sem chamar a API nem fazer broadcast). */
  clear: () => void;
  /** Renova os tokens via POST /auth/refresh. Distingue inatividade de outras falhas. */
  refreshSession: () => Promise<RefreshResult>;
  /** Encerra a sessão no backend (POST /auth/logout), limpa o estado e propaga LOGOUT. */
  logout: () => Promise<void>;
}

export const useAuthStore = create<AuthState>()(
  persist(
    (set, get) => ({
  access_token: null,
  refresh_token: null,
  usuario: null,
  autenticado: false,
  trial_expirado: false,
  senha_provisoria: false,

  setSession: ({ access_token, refresh_token, usuario, trial_expirado, trocar_senha_obrigatoria }) => {
    // [AB#1173] Entrou outra pessoa (ou outro tenant, via impersonate): nenhum arquivo de carga da sessão anterior fica.
    const tenantAntes = tenantDoTokenAtual(get().access_token);
    if (get().usuario?.id !== usuario.id || (tenantAntes !== 0 && tenantAntes !== tenantDoTokenAtual(access_token))) {
      void limparTodosArquivos();
    }
    set({
      access_token,
      refresh_token,
      usuario,
      autenticado: true,
      trial_expirado: trial_expirado ?? false,
      senha_provisoria: trocar_senha_obrigatoria ?? false,
    });
  },

  setTrialExpirado: (v) => set({ trial_expirado: v }),
  setSenhaProvisoria: (v) => set({ senha_provisoria: v }),

  clear: () => {
    // [AB#1173] Logout / sessão inválida / fim de impersonate: apaga os arquivos de carga guardados no navegador.
    void limparTodosArquivos();
    set({
      access_token: null,
      refresh_token: null,
      usuario: null,
      autenticado: false,
      trial_expirado: false,
      senha_provisoria: false,
    });
  },

  async refreshSession(): Promise<RefreshResult> {
    const refresh_token = get().refresh_token;
    if (!refresh_token) return { ok: false, reason: 'invalid' };
    try {
      const res = await fetch(`${API_URL}/auth/refresh`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ refresh_token }),
      });
      if (!res.ok) {
        // O motivo da falha vem na mensagem: inatividade vs sessão inválida.
        const body: unknown = await res.json().catch(() => null);
        const msg =
          body && typeof body === 'object' && 'message' in body
            ? String((body as { message: unknown }).message)
            : '';
        get().clear();
        return /inatividade/i.test(msg)
          ? { ok: false, reason: 'inactivity' }
          : { ok: false, reason: 'invalid' };
      }
      const data = (await res.json()) as {
        access_token: string;
        refresh_token: string;
        trocar_senha_obrigatoria?: boolean;
      };
      set({
        access_token: data.access_token,
        refresh_token: data.refresh_token,
        senha_provisoria: Boolean(data.trocar_senha_obrigatoria),
      });
      return { ok: true };
    } catch {
      get().clear();
      return { ok: false, reason: 'invalid' };
    }
  },

  async logout(): Promise<void> {
    const access_token = get().access_token;
    try {
      if (access_token) {
        await fetch(`${API_URL}/auth/logout`, {
          method: 'POST',
          headers: { 'Content-Type': 'application/json', Authorization: `Bearer ${access_token}` },
        });
      }
    } catch {
      // best-effort: o backend é a fonte da verdade, mas seguimos limpando local.
    }
    get().clear();
    // Propaga para outras abas (REQ-003 / AB#25).
    broadcastAuth('LOGOUT');
    },
  }),
  {
    name: 'auth',
    storage: createJSONStorage(() => localStorage),
    // Não persiste access_token (REQ-003 RN-05) — só o que sobrevive a F5.
    partialize: (state) => ({
      refresh_token: state.refresh_token,
      usuario: state.usuario,
      autenticado: state.autenticado,
      // [Bug#357] Persistido pra sobreviver ao F5 — sem isso o TenantLayout não
      // sabe o estado no primeiro render e o flash volta. É cache; o /auth/me do
      // shell corrige se estiver defasado.
      trial_expirado: state.trial_expirado,
      senha_provisoria: state.senha_provisoria,
    }),
    // [fix cold-start] A detecção de "hidratação concluída" é feita no
    // App.tsx via `useAuthStore.persist.hasHydrated()` +
    // `onFinishHydration` (API oficial do zustand-persist v4). Não
    // precisamos de flag custom no estado nem de `onRehydrateStorage`
    // — esses utils foram desenhados exatamente pra esse caso e
    // funcionam tanto com snapshot existente quanto sem.
  },
  ),
);
